import { Guid } from "../Guid";
import { SubscriptionPlanDto } from "./ClinicConfig/SubscriptionPlanDto";
import { TaxRule } from "./Common/TaxRule";
import { PortalRegistrationState } from "./Enums/PortalRegistrationState";
import { PatientProfileMyleDto, SharedPatientMyleDto } from "./Patient/SharedPatientMyleDto";
import { CivilState, ContactType, GenderIdentity, MedicalSex, PaymentCategory, PaymentInterval, PaymentStatus, PaymentType, PreferredPronouns, SubscriptionPaymentType, SubscriptionStatus, TaxType, TelephoneNumberType, TransactionStatus } from "./MedfarPortalEnums";

// Initially generated from ServiceStack - todo: convert these to MYLE-controled dtos
// @Flags()
enum UserPermissionFlags {
    None = 0,
    Dossier = 1,
    Document = 2,
    Message = 4,
    Profile = 8,
    Appointment = 16,
    Subscription = 32,
    Billing = 64,
    FullControl = -1
}

class PatientSummaryDto {
    public PatientProfileId: string;
    public OwnerUserId: string;
    public FirstName: string;
    public PreferredName: string;
    public LastName: string;
    public FullName: string;
    public DateOfBirth: string;
    public PreferredLanguage: string;
}

class SharedPatientDto extends PatientSummaryDto {
    public SharedWithUserId: string;
    public FileIndex: number;
    public PermissionFlags: UserPermissionFlags;
    public IsGuardian: boolean;
}

class PatientItemDto {
    public PatientProfileId: string;
}

class MailingAddressDto extends PatientItemDto {
    public MailingAddressId: string;
    public Company: string;
    public StreetAddress: string;
    public City: string;
    public CountryCode: string;
    public Province: string;
    public PostCode: string;
    public ContactType: ContactType;
    public IsPrimary: boolean;
}

class PaymentGatewayPaymentProfileDto {
    public PaymentGatewayCustomerId: string;
    public PaymentGatewayPaymentProfileId: string;
    public IsDefault: boolean;
    public PaymentType: PaymentType;
    public CardNumber: string;
    public DisplayName: string;
    public ExpirationDate: string;
    public ExpirationDisplay: string;
    public ExpirationMonth: number;
    public ExpirationYear: number;
    public CardholderName: string;
    public BillingAddress: MailingAddressDto;
}

class PaymentGatewayCustomerDto {
    public PaymentGatewayCustomerId: string;
    public Email: string;
    public Description: string;
    public PaymentProfiles: PaymentGatewayPaymentProfileDto[];
    public PaymentGatewaySubscriptionIds: string[];
}

class PatientDemographicsDto extends PatientSummaryDto {
    public Sex: MedicalSex;
    public GenderIdentity: GenderIdentity;
    public PreferredPronouns: PreferredPronouns;
    public SpecifiedPreferredPronouns: string;
    public CivilState: CivilState;
    public InsuranceNumber: string;
    public SubscriptionGroupId: string;
}

class TelephoneNumberDto extends PatientItemDto {
    public TelephoneNumberId: string;
    public CountryCode: string;
    public Number: string;
    public Extension: string;
    public NumberType: TelephoneNumberType;
    public ContactType: ContactType;
    public IsPrimary: boolean;
    public ContactPointTypeLookupCode: string;
    public PhoneTypeLookupCode: string;
}

class EmailAddressDto extends PatientItemDto {
    public ContactType: ContactType;
    public IsPrimary: boolean;
    public EmailAddressId: string;
    public Email: string;
    public NotificationsEnabled: boolean;
}

class PatientProfileDto extends PatientDemographicsDto {
    public TelephoneNumbers: TelephoneNumberDto[];
    public MailingAddresses: MailingAddressDto[];
    public EmailAddresses: EmailAddressDto[];
}

class PaymentTransactionItemDto {
    public ItemId: string;
    public Name: string;
    public Description: string;
    public Quantity: number;
    public Taxable: boolean;
    public UnitPrice: number;
    public ApplicableTaxes: TaxRule[];
    public Price: number;
}

class TaxAmount {
    public TaxType: TaxType;
    public Amount: number;
}

class TotalWithTax {
    public Subtotal: number;
    public TaxAmounts: TaxAmount[];
    public Tax: number;
    public Total: number;
}

class SubscriptionGroupPlanDto {
    public SubscriptionGroupPlanId: string;
    public SubscriptionGroupId: string;
    public SubscriptionPlanId: string;
    public SubscriptionPlanName: string;
    public SubscriptionPlanInternalName: string;
    public StartDate: string;
    public EndDate: string;
    public FirstBillDate: string;
    public PaymentInterval: PaymentInterval;
    public PaymentGatewayName: string;
    public PaymentGatewaySubscriptionId: string;
    public ListRate: number;
    public UnitPrice: number;
    public Quantity: number;
    public Taxable: boolean;
    public ApplicableTaxes: TaxRule[];
    public Rate: TotalWithTax;
}

class PaymentDto {
    public PaymentId: string;
    public DateCreated: string;
    public BillDate: string;
    public Description: string;
    public InternalDescription: string;
    public PaymentCategory: PaymentCategory;
    public RefundsPaymentId: string;
    public PaymentStatus: PaymentStatus;
    public ListPrice: number;
    public UnitPrice: number;
    public Quantity: number;
    public Taxable: boolean;
    public ApplicableTaxes: TaxRule[];
    public Amount: TotalWithTax;
    public PaymentGatewayName: string;
    public PaymentGatewayTransactionId: string;
    public PaymentGatewayTransactionStatus: TransactionStatus;
}

class SubscriptionPaymentDto extends PaymentDto {
    public SubscriptionPaymentType: SubscriptionPaymentType;
    public SubscriptionGroupId: string;
    public SubscriptionGroupPlanId: string;
    public SubscriptionGroupPlanPaymentInterval: PaymentInterval;
    public SubscriptionPlanId: string;
    public PreviousSubscriptionGroupPlanId: string;
    public PreviousSubscriptionPlanId: string;
}

class SubscriptionGroupDtoBase {
    public SubscriptionGroupId: string;
    public ManagerProfileId: string;
    public DateCreated: string;
    public StartDate: string;
    public EndDate: string;
    public Status: SubscriptionStatus;
    public SuspendedUntil: string;
    public SuspendedReason: string;
    public CurrentGroupPlan: SubscriptionGroupPlanDto;
    public UpcomingSubscriptionPayment: SubscriptionPaymentDto;
    public HasUnpaidPayments: boolean;
    public SubscriptionPayments: SubscriptionPaymentDto[];
    public GroupPlans: SubscriptionGroupPlanDto[];
    public UpcomingPayment: SubscriptionPaymentDto;
}

class SubscriptionGroupMemberDto extends PatientSummaryDto {
    public SubscriptionGroupId: string;
    public IsManager: boolean;
}

class PortalUserDto {
    public UserId: string;
    public Email: string;
    public FirstName: string;
    public LastName: string;
    public FullName: string;
    public BirthDate: string;
    public Language: string;
    public IsActive: boolean;
    public IsActivationTokenInvalid: string;
    public LastLogin: string;
    public LastLoginAttempt: string;
    public DateCreated: string;
    public DateActivated: string;
    public LastLocked: string;
    public LockedUntil: string;
}

class LinkedPortalUserDto extends PortalUserDto {
    public PermissionFlags: UserPermissionFlags;
    public IsGuardian: boolean;
    public IsOwner: boolean;
}

class PortalUserWithPermissionsMyleDto extends PortalUserDto {
    public SharedPatients: SharedPatientMyleDto[];
    public ConfirmationCode: string;
    public CreatedOnConfirmationCode: Date;
}

class SubscriptionGroupMemberMyleDto extends SubscriptionGroupMemberDto {
    public MyleId: Guid;
    public MyleType: string;
}

class SubscriptionGroupMyleDto extends SubscriptionGroupDtoBase {
    public Members: SubscriptionGroupMemberMyleDto[];
}

class GetPatientOverviewResponse {
    public Profile: PatientProfileMyleDto;
    public Account: PortalUserWithPermissionsMyleDto;
    public SubscriptionGroup: SubscriptionGroupMyleDto;
    public PaymentCustomer: PaymentGatewayCustomerDto;
    public Subscription: SubscriptionGroupMyleDto;
    public LinkedAccounts: LinkedPortalUserDto[];
    public RegistrationState: PortalRegistrationState;
}

class LinkPatientProfilePreviewResponse {
    public CanBeLinked: boolean;
    public Reason: string;
    public CanTransferDocuments: boolean;
    public Subscription: SubscriptionGroupMyleDto;
    public CanTransferSubscription: boolean;
    public PaymentCustomer: PaymentGatewayCustomerDto;
    public CanTransferPaymentCustomer: boolean;
    public SharedPatients: SharedPatientMyleDto[];
    public CanTransferSharedPatients: boolean;
}

class LinkPatientProfileResponse {
    public AuthCode: string;
    public Patient: GetPatientOverviewResponse;
}

class ChangeSubscriptionResponse {
    public EffectiveGroupPlan: SubscriptionGroupPlanDto;
    public NewGroupPlan: SubscriptionGroupPlanDto;
    public EffectiveDate: string;
    public ChangePayments: SubscriptionPaymentDto[];
}

class GetSubscriptionPlansResponse {
    public Count: number;
    public SubscriptionPlans: SubscriptionPlanDto[];
}

class PaymentGatewayTransactionDto {
    public PaymentGatewayName: string;
    public PaymentGatewayTransactionId: string;
    public PaymentGatewayTransactionDate: string;
    public PaymentGatewayCustomerId: string;
    public PaymentGatewaySubscriptionId: string;
    public PaymentGatewayReferenceId: string;
    public PaymentGatewayBatchId: string;
    public IsSettled: boolean;
    public PaymentProfile: PaymentGatewayPaymentProfileDto;
    public Items: PaymentTransactionItemDto[];
    public Status: TransactionStatus;
    public Description: string;
    public Subtotal: number;
    public Tax: number;
    public Total: number;
    public Amount: number;
}

export { ChangeSubscriptionResponse, EmailAddressDto, GetPatientOverviewResponse, GetSubscriptionPlansResponse, LinkedPortalUserDto, LinkPatientProfilePreviewResponse, LinkPatientProfileResponse, MailingAddressDto, PatientProfileDto, PaymentGatewayCustomerDto, PaymentGatewayPaymentProfileDto, PaymentGatewayTransactionDto, PaymentTransactionItemDto, PortalUserWithPermissionsMyleDto, SharedPatientDto, SubscriptionGroupMemberMyleDto, SubscriptionGroupMyleDto, SubscriptionPaymentDto, TelephoneNumberDto, UserPermissionFlags };
